import React, {useCallback, useEffect, useRef, useState} from "react";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import ReadingArea from "./components/ReadingArea";
import RightPanel from "./components/RightPanel";
import Tooltip from "./components/Tooltip";
import useZustandStore from "./hooks/useZustandStore";
import {useTheme} from "./hooks/useTheme";
import {useFont} from "./hooks/useFont";
import {useBookDetail, useChapter, useProgress} from "./hooks/useBook";
import {usePrefetchChapters} from "./hooks/usePrefetchChapters";
import {saveProgress} from "./api/book";
import NavBtn from "./components/NavBtn";

const DEFAULT_SETTINGS = {
    showTranslation: true,
    showCommentary: true,
    showPinyin: false,
    focusMode: false,
    lineHeight: 2.2,
    fontFamily: "'Noto Serif SC','Songti SC','STSong',serif",
    themeLabel: "宣纸米白",
};

export default React.memo(function App() {
    // 从 store 获取状态和 actions
    const fontSize = useZustandStore((state) => state.fontSize);
    const progress = useZustandStore((state) => state.progress);
    const bookmarked = useZustandStore((state) => state.bookmarked);
    const activeBook = useZustandStore((state) => state.activeBook);
    const activeBookId = useZustandStore((state) => state.activeBookId);
    const activeChar = useZustandStore((state) => state.activeChar);
    const tooltip = useZustandStore((state) => state.tooltip);
    const settings = useZustandStore((state) => state.settings);
    const currentChapter = useZustandStore((state) => state.currentChapter);
    const focusMode = settings.focusMode;

    // 章节相关状态
    const chapters = useZustandStore((state) => state.chapters);
    const activeChapterId = useZustandStore((state) => state.activeChapterId);
    const chapterTitle = useZustandStore((state) => state.chapterTitle);
    const chapterOrd = useZustandStore((state) => state.chapterOrd);
    const chapterTotal = useZustandStore((state) => state.chapterTotal);
    const prevChapterId = useZustandStore((state) => state.prevChapterId);
    const nextChapterId = useZustandStore((state) => state.nextChapterId);

    const setFontSize = useZustandStore((state) => state.setFontSize);
    const setProgress = useZustandStore((state) => state.setProgress);
    const toggleBookmark = useZustandStore((state) => state.toggleBookmark);
    const setActiveBook = useZustandStore((state) => state.setActiveBook);
    const setActiveChar = useZustandStore((state) => state.setActiveChar);
    const setTooltip = useZustandStore((state) => state.setTooltip);
    const updateSetting = useZustandStore((state) => state.updateSetting);
    const setCurrentChapter = useZustandStore((state) => state.setCurrentChapter);
    const setChapters = useZustandStore((state) => state.setChapters);
    const setActiveChapterId = useZustandStore((state) => state.setActiveChapterId);
    const setChapterData = useZustandStore((state) => state.setChapterData);
    const setTheme = useTheme();
    useFont();

    // ── 移动端抽屉状态 ──
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);

    // 选择书籍后自动关闭侧边栏
    const handleSelectBook = useCallback((title, id) => {
        setActiveBook(title, id);
        setSidebarOpen(false);
    }, [setActiveBook]);

    // ── 数据获取：书籍详情（含目录） ──
    const { data: bookDetail } = useBookDetail(activeBookId);

    // 书籍详情返回后：同步章节目录 + 自动打开第一章
    useEffect(() => {
        if (bookDetail?.data?.chapters) {
            const chs = bookDetail.data.chapters;
            setChapters(chs);
            // 自动选中第一章（仅在无选中或切换书籍时）
            if (chs.length > 0) {
                setActiveChapterId(chs[0].id);
            }
        }
    }, [bookDetail, setChapters, setActiveChapterId]);

    // ── 数据获取：章节内容 ──
    const { data: chapterData, isLoading: chapterLoading } = useChapter(activeBookId, activeChapterId);

    // 章节内容返回后：同步到 store
    useEffect(() => {
        if (chapterData?.data) {
            setChapterData(chapterData.data);
        }
    }, [chapterData, setChapterData]);

    // ── 预加载前后章（命中缓存，切章零等待） ──
    usePrefetchChapters(activeBookId, activeChapterId, chapters);

    // ── 阅读进度：恢复上次位置 ──
    const { data: savedProgress } = useProgress(activeBookId);
    const progressRestoredRef = useRef(false);

    useEffect(() => {
        if (savedProgress?.data && chapters.length > 0 && !progressRestoredRef.current) {
            const { chapter_id, scroll_pct } = savedProgress.data;
            // 恢复上次阅读的章节
            const exists = chapters.find(c => c.id === chapter_id);
            if (exists) {
                setActiveChapterId(chapter_id);
                progressRestoredRef.current = true;
            }
        }
    }, [savedProgress, chapters, setActiveChapterId]);

    // 切换书籍时重置恢复标记
    useEffect(() => {
        progressRestoredRef.current = false;
    }, [activeBookId]);

    // ── 阅读进度：自动保存（防抖 3 秒） ──
    useEffect(() => {
        if (!activeBookId || !activeChapterId) return;
        const timer = setTimeout(() => {
            saveProgress(activeBookId, activeChapterId, progress).catch(() => {});
        }, 3000);
        return () => clearTimeout(timer);
    }, [activeBookId, activeChapterId, progress]);

    // ── URL 路由（hash 同步） ──
    useEffect(() => {
        // 从 URL hash 恢复状态: #/book/2/chapter/10
        const hash = window.location.hash;
        const m = hash.match(/^#\/book\/(\d+)\/chapter\/(\d+)$/);
        if (m) {
            const bid = parseInt(m[1]), cid = parseInt(m[2]);
            if (bid && bid !== activeBookId && books?.data) {
                // 从已有数据中找书名
                for (const cat of books.data) {
                    for (const b of cat.Books) {
                        if (b.ID === bid) {
                            setActiveBook(b.Title, bid);
                            break;
                        }
                    }
                }
            }
            if (cid && chapters.length > 0) {
                setActiveChapterId(cid);
            }
        }
    }, []); // 仅 mount 时执行一次

    useEffect(() => {
        if (activeBookId && activeChapterId) {
            window.location.hash = `#/book/${activeBookId}/chapter/${activeChapterId}`;
        }
    }, [activeBookId, activeChapterId]);

    // TODO: 字符释义已由章节接口按需返回（chapterData.data.chars），无需全局加载
    // useEffect(() => {
    //     fetchCharData().catch(error => console.error(error))
    // }, [fetchCharData, activeBookId]);


    // Keyboard shortcuts
    useEffect(() => {
        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            switch (e.key) {
                case 'ArrowLeft':
                    prevChapterId && setActiveChapterId(prevChapterId);
                    break;
                case 'ArrowRight':
                    nextChapterId && setActiveChapterId(nextChapterId);
                    break;
                case '=': case '+':
                    setFontSize(n => Math.min(24, n + 1));
                    break;
                case '-':
                    setFontSize(n => Math.max(13, n - 1));
                    break;
                case 'Escape':
                    updateSetting('focusMode', !focusMode);
                    break;
                default: break;
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [focusMode, updateSetting, setFontSize, prevChapterId, nextChapterId, setActiveChapterId]);

    const handleSettingChange = useCallback((key, val) => {
        updateSetting(key, val);
    }, [updateSetting]);

    const handleCharHover = useCallback((char, e, rect) => {
        setActiveChar(char, e);

        const TOOLTIP_MAX_W = 260;
        const EDGE_PAD = 16;
        // 与 Tooltip 组件一致：桌面 260px，小屏 vw-16px
        const maxW = Math.min(TOOLTIP_MAX_W, window.innerWidth - EDGE_PAD);

        if (rect) {
            const charCenterX = rect.left + rect.width / 2;

            // 水平：tooltip 居中于字符，两端留 EDGE_PAD
            const idealX = charCenterX - maxW / 2;
            const safeX = Math.max(EDGE_PAD, Math.min(idealX, window.innerWidth - maxW - EDGE_PAD));

            // 像素偏移：字符中心相对于 tooltip 左边缘的距离
            const arrowOffset = charCenterX - safeX;

            // tooltip 与字符间距：三角号高 6px + 间距 8px
            const spaceAbove = rect.top;
            const tooltipHeight = 42;
            const GAP = 14; // 三角 6px + 视觉间距 8px
            let safeY;
            if (spaceAbove >= tooltipHeight + GAP) {
                safeY = rect.top - tooltipHeight - GAP;
            } else {
                safeY = rect.bottom + GAP;
            }

            setTooltip(char, {x: safeX, y: safeY, arrowOffset, arrowBelow: spaceAbove < tooltipHeight + GAP});
        } else {
            const safeX = Math.max(EDGE_PAD, Math.min(e.clientX - maxW / 2, window.innerWidth - maxW - EDGE_PAD));
            const safeY = e.clientY - 60;
            setTooltip(char, {x: safeX, y: safeY, arrowOffset: maxW / 2, arrowBelow: false});
        }
    }, [setActiveChar, setTooltip]);

    const handleCharLeave = useCallback(() => {
        setTooltip(null, null);
    }, [setTooltip]);

    return (
        <div
            className="flex h-screen bg-[#2a1f0e] relative overflow-hidden"
            style={{fontFamily: 'var(--app-font-family)'}}
        >
            {/* ── 移动端遮罩 ── */}
            {(sidebarOpen || panelOpen) && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => { setSidebarOpen(false); setPanelOpen(false); }}
                />
            )}

            {/* ── 移动端侧边栏抽屉 ── */}
            <div className={`
                fixed inset-y-0 left-0 z-50 w-[280px] transform transition-transform duration-300
                md:hidden
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <Sidebar
                    focusMode={focusMode}
                    activeBook={activeBook}
                    onSelectBook={handleSelectBook}
                    isMobile={true}
                    onClose={() => setSidebarOpen(false)}
                />
            </div>

            {/* ── 桌面端侧边栏 ── */}
            <div className="hidden md:block">
                <Sidebar
                    focusMode={focusMode}
                    activeBook={activeBook}
                    onSelectBook={setActiveBook}
                />
            </div>

            {/* Progress bar */}
            <div className="fixed top-0 left-0 md:left-sidebar right-0 md:right-panel h-0.5 bg-paper-deep z-50">
                <div
                    className="h-full bg-gradient-to-r from-vermilion to-gold transition-[width] duration-300"
                    style={{width: `${progress}%`}}
                />
            </div>

            {/* Main reading pane */}
            <main className="flex-1 flex flex-col overflow-hidden bg-paper relative min-w-0">
                {/* Paper ruled lines */}
                <div className="pointer-events-none absolute inset-0 paper-lines z-0"/>

                <Topbar
                    focusMode={focusMode}
                    onToggleFocus={() => handleSettingChange("focusMode", !focusMode)}
                    fontSize={fontSize}
                    onFontSize={(d) => setFontSize((n) => Math.min(24, Math.max(13, n + d)))}
                    bookmarked={bookmarked}
                    onToggleBookmark={() => toggleBookmark((b) => !b)}
                    onOpenSidebar={() => setSidebarOpen(true)}
                    onOpenPanel={() => setPanelOpen(true)}
                    chapters={chapters}
                    activeChapterId={activeChapterId}
                    onChapterSelect={setActiveChapterId}
                />

                <ReadingArea
                    fontSize={fontSize}
                    lineHeight={settings.lineHeight}
                    showTranslation={settings.showTranslation}
                    showCommentary={settings.showCommentary}
                    onProgress={setProgress}
                    onCharHover={handleCharHover}
                    onCharLeave={handleCharLeave}
                    isLoading={chapterLoading}
                />

                {/* Chapter navigation */}
                <div
                    className={`
            flex items-center justify-between px-4 md:px-10 h-[52px] flex-shrink-0
            border-t border-paper-deep bg-paper/90 relative z-10
            transition-opacity duration-500
          `}
                    style={{opacity: focusMode ? 0.15 : 1}}
                >
                    <NavBtn
                        label="← 上一章"
                        disabled={!prevChapterId}
                        onClick={() => prevChapterId && setActiveChapterId(prevChapterId)}
                    />
                    <span className="text-[10px] md:text-xs text-ink-faint tracking-[1px] md:tracking-[2px] truncate px-2">
            {chapterTitle || '—'} · 第 {chapterOrd || '—'} / {chapterTotal || '—'} 节
          </span>
                    <NavBtn
                        label="下一章 →"
                        disabled={!nextChapterId}
                        onClick={() => nextChapterId && setActiveChapterId(nextChapterId)}
                    />
                </div>
            </main>

            {/* ── 移动端右侧面板 ── */}
            <div className={`
                fixed inset-y-0 right-0 z-50 w-[300px] transform transition-transform duration-300
                md:hidden
                ${panelOpen ? 'translate-x-0' : 'translate-x-full'}
            `}>
                <RightPanel
                    focusMode={focusMode}
                    activeChar={activeChar}
                    settings={settings}
                    onSettingChange={handleSettingChange}
                    onTheme={setTheme}
                    currentChapter={currentChapter}
                    onChapterSelect={(id) => { setActiveChapterId(id); setPanelOpen(false); }}
                    chapters={chapters}
                    activeChapterId={activeChapterId}
                    isMobile={true}
                    onClose={() => setPanelOpen(false)}
                />
            </div>

            {/* ── 桌面端右侧面板 ── */}
            <div className="hidden md:block flex-shrink-0">
                <RightPanel
                    focusMode={focusMode}
                    activeChar={activeChar}
                    settings={settings}
                    onSettingChange={handleSettingChange}
                    onTheme={setTheme}
                    currentChapter={currentChapter}
                    onChapterSelect={setActiveChapterId}
                    chapters={chapters}
                    activeChapterId={activeChapterId}
                />
            </div>

            {/* Floating tooltip */}
            <Tooltip char={tooltip.char} pos={tooltip.pos}/>
        </div>
    );
});
